<template>
  <component-wrapper
    v-if="notAssistedServiceUser"
    class="assisted-service-module-login-fi"
    data-test="assisted-service-module-login"
  >
    <grid-container-fi :container="false">
      <div
        class="assisted-service-module-login-fi__content fi-col-span-12 sm:fi-col-span-10 sm:fi-col-start-2 md:fi-col-span-8 md:fi-col-start-3 lg:fi-col-span-6 lg:fi-col-start-4 xl:fi-col-span-4 xl:fi-col-start-5"
      >
        <div v-if="isLoginFailure">
          <error-message
            ref="errorMessageRef"
            :title="fields.errorMessageTitle.value"
            trigger-on-creation
          >
            {{ fields.errorMessageBody.value }}
          </error-message>
        </div>
        <headline-fi
          class="assisted-service-module-login-fi__headline"
          headline-tag="h1"
        >
          {{ fields.title.value }}
        </headline-fi>
        <template v-if="isLoginPrompt">
          <form
            ref="loginForm"
            method="POST"
            :action="isMounted ? loginAction : undefined"
          >
            <text-input-fi
              v-model.trim="username"
              :label="fields.usernameFieldLabel.value"
              class="assisted-service-module-login-fi__username"
              data-test="login-username"
              name="username"
              large
              @autofilled="enableSubmitBtn"
              @keyup-enter="onEnterEmail"
            />
            <text-input-fi
              ref="passwordRef"
              v-model="password"
              :label="fields.passwordFieldLabel.value"
              class="assisted-service-module-login-fi__password"
              type="password"
              data-test="login-password"
              name="password"
              large
              @autofilled="enableSubmitBtn"
              @keyup-enter="onEnterPassword"
            />
            <input
              type="hidden"
              name="brand"
              :value="isMounted ? getBrandParam() : undefined"
            />
            <input
              type="hidden"
              name="authorize_redirect"
              :value="fischer.query('authorize_redirect')"
            />
            <button-fi
              type="button"
              data-test="login-submit"
              :class="[
                'button-fi__primary',
                'button-fi--large',
                'button-fi--stretched',
                'assisted-service-module-login-fi__submit',
              ]"
              :disabled="!isMounted"
              @click="onLogin"
            >
              {{ fields.loginButtonTitle.value }}
            </button-fi>
          </form>
          <component
            :is="DevAssistedServiceLoginFi"
            v-if="DevAssistedServiceLoginFi"
            @select="onDevUserSelect"
          />
          <button-fi
            v-if="fields.enableSso?.value && ssoUrl"
            data-test="login-sso"
            :href="ssoUrl"
            natural-events
            :class="[
              'button-fi__tertiary',
              'button-fi--large',
              'button-fi--stretched',
              'fi-mt-30',
              'assisted-service-module-login-fi__sso',
            ]"
          >
            {{ fields.ssoLoginLinkTitle?.value }}
          </button-fi>
        </template>
        <template v-else>
          <button-fi
            v-if="fields.enableSso?.value && ssoUrl"
            data-test="login-sso"
            :href="ssoUrl"
            natural-events
            :class="[
              'button-fi__primary',
              'button-fi--large',
              'button-fi--stretched',
              'fi-mt-30',
              'assisted-service-module-login-fi__sso',
            ]"
          >
            {{ fields.ssoLoginLinkTitle?.value }}
          </button-fi>
          <button-fi
            v-if="showManualLogin"
            type="button"
            data-test="login-submit"
            :class="[
              'button-fi__tertiary',
              'button-fi--large',
              'button-fi--stretched',
              'fi-mt-30',
              'assisted-service-module-login-fi__submit',
            ]"
            @click="onSubmit"
          >
            {{ 'login with username / password' }}
          </button-fi>
        </template>
        <component
          :is="DevSalesforceLinksFi"
          v-if="DevSalesforceLinksFi && showDevSalesforceLinks"
        />
      </div>
    </grid-container-fi>
  </component-wrapper>
</template>

<script setup lang="ts">
import { ButtonFi, ErrorMessage, HeadlineFi, TextInputFi } from 'atoms';
import { computed, defineAsyncComponent, nextTick, onMounted, ref, useTemplateRef } from 'vue';

import { useAssistedServiceLogin } from '@/components/assisted-service-module-fi/assistedServiceLogin';
import ComponentWrapper from '@/components/component-wrapper/ComponentWrapper.vue';
import type { DevAssistedServiceUser } from '@/components/dev/dev-assisted-service-login-fi/devAssistedServiceUser';
import GridContainerFi from '@/components/grid-fi/GridContainerFi.vue';
import { useCookies } from '@/composables/cookies';
import { useFischer } from '@/composables/fischerPlugin';
import { useLogin } from '@/composables/login';
import { salesforceItemName } from '@/lib/fischerPlugin';
import { postFormEncoded } from '@/lib/postFormEncoded';

defineOptions({
  name: 'AssistedServiceModuleLoginFi',
});

// showroom-only dev tool; IS_SHOWROOM is a compile-time constant, so this whole
// branch and the imported chunk are dead code in every real build
const DevAssistedServiceLoginFi = IS_SHOWROOM
  ? defineAsyncComponent(
      () => import('@/components/dev/dev-assisted-service-login-fi/DevAssistedServiceLoginFi.vue'),
    )
  : null;

const DevSalesforceLinksFi = IS_SHOWROOM
  ? defineAsyncComponent(
      () => import('@/components/dev/dev-salesforce-links-fi/DevSalesforceLinksFi.vue'),
    )
  : null;

const samlQueryParam = 'saml_authorization_code';
const previousAttemptFilter = new RegExp(`(?:^|&)${samlQueryParam}=[^&]*`);

const salesforceParams = [
  'orgUnitId',
  'distributionChannel',
  'division',
  'salesforceOrderId',
  'salesforceQuoteId',
  'lang',
  'orderIdDownloads',
];

interface Fields {
  errorMessageTitle: { value: string };
  errorMessageBody: { value: string };
  title: { value: string };
  usernameFieldLabel: { value: string };
  passwordFieldLabel: { value: string };
  loginButtonTitle: { value: string };
  enableSso?: { value: boolean };
  ssoLoginLinkTitle?: { value: string };
}

interface Props {
  fields: Fields;
}

defineProps<Props>();

const fischer = useFischer();
const { cookies } = useCookies();
const { postAdminTokenInSession } = useAssistedServiceLogin();
const { createChallenge } = useLogin();

const isLoginFailure = ref(false);
const username = ref('');
const password = ref('');
const enableSubmit = ref(false);
const submitting = ref(false);
const notAssistedServiceUser = ref(true);
const isMounted = ref(false);

const errorMessageRef = ref<any>(null);
const passwordRef = ref<any>(null);

/**
 * Both branches wait for the mount to stay in step with the SSO button, whose ssoUrl needs
 * window.location. Not because the params are missing server-side - they are there, see
 * fischerPlugin's ssrQuery.
 */
const isLoginPrompt = computed(
  () => isMounted.value && Boolean(fischer.query('brand')?.includes('@aslogin')),
);

const generateSsoUrl = (): string => {
  const params = new URLSearchParams();

  const query = (fischer.query('query') || '').replace(previousAttemptFilter, '');
  if (query) {
    params.append('query', query);
  }

  const salesforce = fischer.query('salesforce') || fischer.query('salesforce-callback');
  if (salesforce) {
    params.append('salesforce-callback', salesforce);
    salesforceParams.forEach((key) => {
      const value = fischer.query(key);
      if (value) {
        params.append(`salesforce-${key}`, value);
      }
    });
  } else {
    params.append('sso_callback', 'true');
  }

  params.append('site', IS_SHOWROOM ? 'local' : fischer.baseSiteId());

  return `${import.meta.env.VITE_API_URL}/samlsinglesignon/saml${window.location.pathname}?${params}`;
};

const showManualLogin = computed(
  () => isMounted.value && Boolean(fischer.query('manual-login') || IS_SHOWROOM),
);

const ssoUrl = computed(() => {
  if (!isMounted.value) {
    return null;
  }
  return generateSsoUrl();
});

// the links start a fresh salesforce flow, so they only make sense on a bare url
const showDevSalesforceLinks = computed(() => isMounted.value && !window.location.search);

const addQueryToRedirect = (page: string) => {
  const query = fischer.query('query');
  return query ? `${page}?query=${query}` : page;
};

const onEnterEmail = () => {
  if (!username.value) {
    return;
  }
  passwordRef.value?.focus();
};

const onEnterPassword = () => {
  if (!password.value) {
    return;
  }
  onLogin();
};

const loginAction = import.meta.env.VITE_ACC_PAGE;
const loginForm = useTemplateRef('loginForm');
const onLogin = () => {
  loginForm.value?.submit();
};

const onDevUserSelect = async (user: DevAssistedServiceUser) => {
  username.value = user.username;
  password.value = user.password;
  // the form posts the real DOM values, so wait for v-model to flush
  await nextTick();
  onLogin();
};

const getRedirectUri = () => `${fischer.hostUrlBase()}${fischer.uri()}`;

const getBrandParam = () => `dialog:${window.location.pathname}@aslogin`;

const onSubmit = async () => {
  try {
    submitting.value = true;
    const { state, verifier, challenge } = await createChallenge();

    const data: Record<string, string> = {
      verifier,
    };

    [...salesforceParams, 'manual-login', 'salesforce'].forEach((key) => {
      const value = fischer.query(key);
      if (value) {
        data[key] = value;
      }
    });

    cookies.set(
      `asm-login-${state}`,
      data,
      '1h',
      fischer.uri(),
      IS_SHOWROOM ? undefined : fischer.hostname(),
      fischer.isSecureConnection(),
      'Lax',
    );

    window.location.href = `${import.meta.env.VITE_API_URL}/authorizationserver/oauth/authorize?${new URLSearchParams(
      {
        response_type: 'code',
        client_id: 'asm',
        redirect_uri: getRedirectUri(),
        scope: 'openid',
        state,
        code_challenge: challenge,
        code_challenge_method: 'S256',
        brand: getBrandParam(),
      },
    )}`;
  } catch (error) {
    console.error(error);
    if (errorMessageRef.value) {
      errorMessageRef.value.trigger();
    } else {
      isLoginFailure.value = true;
    }
  } finally {
    submitting.value = false;
  }
};

const processSalesforceParam = (salesforceCart?: any, cookie?: Record<string, string>) => {
  const salesforceCallback = fischer.query('salesforce-callback') || cookie?.salesforce;
  const salesforce = salesforceCallback || fischer.query('salesforce') === 'true';
  if (salesforce) {
    const salesforceItem: Record<string, any> = {};
    if (salesforceCart) {
      salesforceItem.cart = salesforceCart;
    }
    salesforceParams.forEach((key) => {
      const value = cookie
        ? cookie?.[key]
        : fischer.query(salesforceCallback ? `salesforce-${key}` : key);
      if (value) {
        salesforceItem[key] = value;
      }
    });
    if (!salesforceItem.salesforceQuoteId && !salesforceItem.salesforceOrderId) {
      const oldSalesforceJson = window.sessionStorage.getItem(salesforceItemName);
      const oldSalesforce = oldSalesforceJson && JSON.parse(oldSalesforceJson);
      if (oldSalesforce) {
        salesforceParams.forEach((key) => {
          if (!salesforceItem[key] && oldSalesforce[key]) {
            salesforceItem[key] = oldSalesforce[key];
          }
        });
      }
    }
    window.sessionStorage.setItem(salesforceItemName, JSON.stringify(salesforceItem));
  } else {
    clearSalesforce();
  }
};

const clearSalesforce = () => {
  try {
    window.sessionStorage.removeItem(salesforceItemName);
  } catch (_error) {
    // noop
  }
};

const handleSsoCallback = async () => {
  try {
    const code = fischer.query('code');
    if (code) {
      const state = fischer.query('state');
      const cookie = cookies.get(`asm-login-${state}`);
      if (!cookie) {
        console.error('state not found');
        isLoginFailure.value = true;
        return;
      }
      const response = await postFormEncoded(
        `${import.meta.env.VITE_API_URL}/authorizationserver/oauth/token`,
        {
          grant_type: 'authorization_code',
          client_id: 'asm',
          code,
          code_verifier: cookie.verifier,
          redirect_uri: getRedirectUri(),
        },
      );
      const json = await response.json();
      const token = json.access_token;
      const idToken = json.id_token;
      const expiresIn = json.expires_in;
      if (!token || !idToken || !expiresIn) {
        throw new Error('Missing tokens in response');
      }
      const { LoginSuccessPage: loginSuccessPage = '/' } = await postAdminTokenInSession(
        token,
        idToken,
        json.refresh_token,
      );

      fischer.setAssistedService({
        token,
        expiresIn: String(expiresIn),
        idToken,
        loginSuccessPage: loginSuccessPage,
        user: undefined,
      });

      if (cookie.salesforce === 'true') {
        processSalesforceParam(null, cookie);
      }
      window.location.href = addQueryToRedirect(loginSuccessPage);
      return;
    }

    const samlToken = fischer.query('saml_token');
    if (samlToken) {
      const response = await postFormEncoded(
        `${import.meta.env.VITE_API_URL}/authorizationserver/oauth/token`,
        {
          grant_type: 'saml_token',
          client_id: 'asm',
          saml_token: samlToken,
          scope: 'openid',
        },
      );
      const json = await response.json();

      const token = json.access_token;
      const idToken = json.id_token;
      const expiresIn = json.expires_in;
      if (!token || !idToken || !expiresIn) {
        throw new Error('Missing tokens in response');
      }
      const { LoginSuccessPage: loginSuccessPage } = await postAdminTokenInSession(
        token,
        idToken,
        json.refresh_token,
      );

      fischer.setAssistedService({
        token,
        expiresIn: String(expiresIn),
        idToken,
        loginSuccessPage: loginSuccessPage || '',
        user: undefined,
      });

      window.location.href = addQueryToRedirect(loginSuccessPage || '/');
    }
  } catch (error) {
    console.error('error handling sso callback');
    console.error(error);
    notAssistedServiceUser.value = true;
    isLoginFailure.value = true;
  }
};

const enableSubmitBtn = (value: boolean) => {
  enableSubmit.value = value;
};

// this unfortunately is required for the loginSuccessPagee
const repostAccessToken = async () => {
  if (IS_SHOWROOM) {
    window.location.href = '/assisted-service/assisted-service-account-management';
  } else {
    const { LoginSuccessPage: loginSuccessPage = '/' } = await postAdminTokenInSession(
      fischer.user.accessToken(),
      fischer.user.idToken(),
    );
    window.location.href = loginSuccessPage;
  }
};

onMounted(() => {
  processSalesforceParam();
  if (
    fischer.user.getAssistedService() &&
    !(fischer.query('salesforce') || fischer.query('salesforce-callback'))
  ) {
    notAssistedServiceUser.value = true;
    const page = fischer.pages.loginSuccessPage().url;
    window.location.href = addQueryToRedirect(page);
    return;
  }

  if (fischer.query('salesforce')) {
    if (fischer.user.getAssistedService()) {
      console.debug('already an assisted service user');
      repostAccessToken();
      return;
    }
    if (fischer.query('manual-login') !== 'true') {
      window.location.href = generateSsoUrl();
      return;
    }
  }

  if (fischer.query('authentication_error') === 'true') {
    isLoginFailure.value = true;
  }

  handleSsoCallback();

  isMounted.value = true;
});
</script>

<style scoped src="./assisted-service-module-login-fi" />
